/**
 * @fileoverview Violation capture surface (per failing mapping).
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - When a widget's `violationMappings[]` evaluate as failing (per
 *   `getFailingMappings()`), render ONE `<ViolationCaptureBlock>` per
 *   matching mapping (B2 multi-mapping).
 * - Surface the per-mapping completion gates (`requireNotes`,
 *   `requirePhotos`, `requireLocation`, `requireCorrectiveAction`)
 *   as inline form fields with `aria-invalid` cues.
 * - Allow the inspector to capture N occurrences per violation
 *   (G-001 multi-occurrence) via "Add Occurrence" / "Remove" rows.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `AttachmentPicker` contract (host-injected) for photo capture.
 * - `LoggedViolation` / `LoggedViolationOccurrence` (renderer-local).
 *
 * Architecture:
 * - Presentation-only. Holds no state beyond the local "draft occurrence"
 *   form and per-occurrence expand state. Persisted state flows through
 *   `onChange(next)` to the host PersistAdapter (Phase 5).
 * - G-023 — the occurrences live nested under the failing response, not in
 *   a separate collection. The adapter (Phase 5) writes
 *   `CanonicalQuestionResponse.violation.occurrences`.
 *
 * Notes:
 * - Occurrence rows are collapsed by default (location + status badges +
 *   actions on one line). Tapping a row expands the full detail inline.
 * - The full-width add-another action identifies its violation code.
 * - Multiple occurrences are enabled unless a mapping explicitly disables them.
 *
 * @component ViolationCaptureBlock
 *
 * @contract ViolationCaptureBlockProps
 * Purpose: prop contract for the per-mapping violation surface.
 * Fields: field/form ids, mapping and current violation, actor/event context,
 * read-only/failure gates, attachment picker, and change callback.
 * Rules: multiple occurrences default to enabled; additions require a failing,
 * writable field.
 * Sync: parent owns persistence through the inspection renderer adapter.
 */
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { ChevronRight, ChevronDown, Plus, Pencil, Camera, AlertTriangle, X as XIcon } from 'lucide-react';
import type { ViolationMapping, AttachmentRefLike } from '@firesquirrel/core-domain';
import {
  applyOccurrenceCorrection,
  clearOccurrenceCorrection,
  removeOccurrenceFromViolation,
} from '@firesquirrel/core-domain';
import type { LoggedViolation, LoggedViolationOccurrence } from './types';
import { UNMAPPED_FAILURE_CODE_SET_ID } from './findViolationForMapping';
import type { AttachmentPicker } from '../contracts/attachmentPicker';
import { OccurrenceRepairBlock } from './OccurrenceRepairBlock';
import type { OccurrenceRepairData } from './OccurrenceRepairModal';
import { TypeYesConfirmModal } from './TypeYesConfirmModal';
import { PhotoLightbox } from './PhotoLightbox';
import { AttachmentThumb } from '../fields/AttachmentThumb';
import {
  useInspectionAuditEvent,
  useInspectionTelemetryEvent,
  useThumbnailResolver,
} from '../contracts/rendererContext';
import { isMultiOccurrenceAllowed } from './multiOccurrence';

/**
 * @contract ViolationCaptureBlockProps
 * Purpose: Describes violation capture block props at the shared type boundary.
 * Fields: fieldId, formId, mapping, violation, inspectorId, eventIndex, readOnly, allowNewOccurrence, isOffline, attachmentPicker, isFailing, showNotes, showPhotos, requireNotes, requirePhotos, quickPickLocations, quickPickRemedies, showLocation, showCorrectiveAction, onChange
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export interface ViolationCaptureBlockProps {
  fieldId: string;
  formId: string;
  /** The mapping that triggered this capture surface. */
  mapping: ViolationMapping;
  /** Existing violation entry (if user already started capturing). */
  violation: LoggedViolation | null;
  /** Inspector id used as `discoveredBy` on new occurrences. */
  inspectorId: string;
  /** Current event index (0-based). */
  eventIndex: number;
  /** Read-only mode (bulk-pilot, NA, or cross-form). */
  readOnly?: boolean;
  /** When false, existing occurrences remain editable but the draft add form is hidden. */
  allowNewOccurrence?: boolean;
  /** Offline flag — surfaces an offline pill on attachment row. */
  isOffline?: boolean;
  /** Host-injected attachment surface. */
  attachmentPicker?: AttachmentPicker;
  /** Whether the parent field is currently in a failing state. */
  isFailing?: boolean;
  /** Field-level optional capture visibility computed by the parent renderer. */
  showNotes?: boolean;
  showPhotos?: boolean;
  requireNotes?: boolean;
  requirePhotos?: boolean;
  /** Field-configured quick-pick location suggestions. */
  quickPickLocations?: readonly string[];
  /** Field-configured quick-pick corrective-action suggestions. */
  quickPickRemedies?: readonly string[];
  /** Deprecated compatibility prop; location capture is always visible. */
  showLocation?: boolean;
  showCorrectiveAction?: boolean;
  /** Persist the updated violation (or null when removed). */
  onChange: (next: LoggedViolation | null) => void;
}

function genId(): string {
  // Local-only id generator. Hosts that need real UUIDs can swap via crypto.
  if (typeof globalThis !== 'undefined' && typeof (globalThis.crypto as Crypto | undefined)?.randomUUID === 'function') {
    return (globalThis.crypto as Crypto).randomUUID();
  }
  return `loc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeEmptyOccurrence(inspectorId: string, eventIndex: number): LoggedViolationOccurrence {
  return {
    _id: genId(),
    status: 'open',
    discoveredInEventIndex: eventIndex,
    attachments: [],
    discoveredAt: new Date().toISOString(),
    discoveredBy: inspectorId,
  };
}

function makeEmptyViolation(p: { fieldId: string; formId: string; mapping: ViolationMapping; eventIndex: number }): LoggedViolation {
  return {
    _id: genId(),
    fieldId: p.fieldId,
    formId: p.formId,
    codes: [{ code: p.mapping.code, codeSetId: p.mapping.codeSetId }],
    severity: p.mapping.severity ?? 'major',
    status: 'open',
    discoveredInEventIndex: p.eventIndex,
    source: 'question',
    occurrences: [],
    mapping: p.mapping,
  };
}

function getViolationStatus(occurrences: LoggedViolationOccurrence[]): LoggedViolation['status'] {
  return occurrences.length > 0 && occurrences.every((o) => o.status === 'resolved' || o.status === 'closed')
    ? 'resolved'
    : 'open';
}

/**
 * @component ViolationCaptureBlock
 * Purpose: Captures and repairs occurrences for one failing question mapping.
 * Props: ViolationCaptureBlockProps.
 * Behavior: Keeps saved occurrence summaries compact and expands details inline.
 * Data Source: local state supplied by the parent renderer's persisted values.
 * Constraints: Mutations and repair/removal semantics use core-domain helpers.
 * Performance: Renders only occurrences attached to the active mapping.
 */
export function ViolationCaptureBlock(props: ViolationCaptureBlockProps): ReactNode {
  const { fieldId, formId, mapping, violation, inspectorId, eventIndex,
    readOnly, isOffline, attachmentPicker, isFailing = true,
    showNotes = true, showPhotos = true,
    requireNotes: propRequireNotes, requirePhotos: propRequirePhotos,
    allowNewOccurrence = true, showCorrectiveAction = true,
    quickPickLocations = [], quickPickRemedies = [], onChange } = props;

  const [draft, setDraft] = useState<LoggedViolationOccurrence>(() => makeEmptyOccurrence(inspectorId, eventIndex));
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isPhotosOpen, setIsPhotosOpen] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  // After the first occurrence is logged the draft form collapses; re-open
  // it explicitly via the "Add another occurrence" button. Initially false
  // because the very-first capture (no occurrences yet) takes the
  // "never logged" branch below, which renders the form regardless of
  // this flag.
  const [isDraftOpen, setIsDraftOpen] = useState(false);
  // Occurrence id queued for confirmed removal. When non-null, the
  // <TypeYesConfirmModal> is shown and blocks removal until the inspector
  // types "yes" and clicks confirm.
  const [pendingRemovalId, setPendingRemovalId] = useState<string | null>(null);
  // Set of occurrence ids whose detail rows are expanded.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Active full-screen photo viewer — set of attachments + the index tapped.
  const [lightbox, setLightbox] = useState<{ attachments: AttachmentRefLike[]; index: number } | null>(null);

  const thumbnailResolver = useThumbnailResolver();
  const emitInspectionAuditEvent = useInspectionAuditEvent();
  const emitInspectionTelemetryEvent = useInspectionTelemetryEvent();
  const canShowThumbnails = !!thumbnailResolver;

  // P7 — retracted occurrences are kept on the violation (audit tombstone that
  // survives the completion rollup) but must NOT surface in the inspector's
  // working list or counts. Repaired occurrences stay visible (shown as
  // "Repaired"); only `retracted` is hidden.
  const visibleOccurrences = violation?.occurrences.filter((o) => o.correctionStatus !== 'retracted') ?? [];
  const occurrenceCount = visibleOccurrences.length;
  const hasOccurrences = occurrenceCount > 0;
  const allowMultiple = isMultiOccurrenceAllowed(mapping);

  // Single-instance mappings (the common case — most question-triggered
  // violations only ever have ONE occurrence) edit that occurrence directly
  // through the top action bar rather than a second, disconnected draft.
  // Without this, re-opening an inspection with an existing occurrence (or
  // typing again after the first auto-commit) silently created an orphaned
  // "phantom" note that never saved — the exact "I can add 2 notes" bug.
  // On mount (or whenever a single occurrence first appears), seed the local
  // draft from it ONCE so further edits update that same occurrence.
  const seededFromOccurrenceRef = useRef(false);
  useEffect(() => {
    if (allowMultiple || seededFromOccurrenceRef.current) return;
    if (visibleOccurrences[0]) {
      setDraft(visibleOccurrences[0]);
      seededFromOccurrenceRef.current = true;
    }
  }, [allowMultiple, visibleOccurrences[0]?._id]);

  // Derive a human-readable label for the violation.
  // mapping.title / mapping.description or extras.legacyViolationTitle (forwarded
  // by FieldViolationCaptures) provides the violation description (e.g. "Zco Test
  // Violation E0111"). Also fall back to violation.codes[0].description so
  // migrated question violations display their description instead of a raw
  // 24-hex codeSetId + code string.
  const mappingWithMeta = mapping as ViolationMapping & { title?: string; codeReference?: string; description?: string };
  const isUnmapped = mapping.codeSetId === UNMAPPED_FAILURE_CODE_SET_ID;
  const existingCodeDescription = (violation?.codes?.[0] as { description?: string } | undefined)?.description?.trim();
  const rawTitle = mappingWithMeta.title?.trim() || mappingWithMeta.description?.trim() || existingCodeDescription;
  const isNumericTitle = !!rawTitle && /^\d+$/.test(rawTitle);
  const hasDescriptiveTitle = !isNumericTitle && !!rawTitle;
  const violationTitle = isUnmapped
    ? 'Pass / Fail only'
    : (hasDescriptiveTitle
        ? rawTitle!
        : (mappingWithMeta.codeReference && !/^\d+$/.test(mappingWithMeta.codeReference.trim())
            ? mappingWithMeta.codeReference
            : 'Violation'));
  const isHexObjectIdCodeSet = /^[0-9a-f]{24}$/i.test(String(mapping.codeSetId ?? ''));
  const fallbackCodeLabel = isHexObjectIdCodeSet
    ? mapping.code
    : `${mapping.codeSetId} / ${mapping.code}`;
  const codeLabel =
    isUnmapped ||
    (isHexObjectIdCodeSet && hasDescriptiveTitle) ||
    mappingWithMeta.codeReference === violationTitle ||
    mapping.code === violationTitle ||
    isNumericTitle
      ? null
      : (mappingWithMeta.codeReference || fallbackCodeLabel);

  // Repaired / unrepaired counts derived from logged occurrences.
  const unrepairedCount = visibleOccurrences.filter((o) => o.status === 'open').length;
  const repairedCount = visibleOccurrences.filter((o) => o.status === 'resolved' || o.status === 'closed').length;

  // Render the draft form when:
  //  - this is a single-instance mapping (location/corrective-action editing
  //    always targets the ONE occurrence, whether it exists yet or not); OR
  //  - this is the first capture for a multi-instance violation (no
  //    occurrences yet, field is currently failing); OR
  //  - the inspector explicitly opened it via "Add another occurrence".
  const showDraftForm = !readOnly
    && allowNewOccurrence
    && (!allowMultiple || !hasOccurrences || isDraftOpen);

  // The "Add another occurrence" button appears only when:
  //  - there's already at least one logged occurrence,
  //  - the mapping allows multiple instances,
  //  - the field is still in a failing state (allowNewOccurrence captures
  //    that), and
  //  - the draft form isn't already open.
  const showAddAnother = !readOnly
    && hasOccurrences
    && allowMultiple
    && allowNewOccurrence
    && !isDraftOpen;

  const requireNotes = propRequireNotes ?? !!mapping.requireNotes;
  const requirePhotos = propRequirePhotos ?? !!mapping.requirePhotos;
  const requireLocation = !!mapping.requireLocation;
  const requireCorrective = !!mapping.requireCorrectiveAction;

  const notesOk = !requireNotes || !!draft.notes?.trim();
  const photosOk = !requirePhotos || draft.attachments.length > 0;
  const locationOk = !requireLocation || !!draft.location?.trim();
  const correctiveOk = !requireCorrective || !!draft.correctiveActions?.trim();
  const draftSatisfiesGates = notesOk && photosOk && locationOk && correctiveOk;

  const isNotesRequiredUnmet = !readOnly && isFailing && requireNotes && !draft.notes?.trim();
  const isPhotosRequiredUnmet = !readOnly && isFailing && requirePhotos && draft.attachments.length === 0;

  // Auto-save for single-instance mappings only. This is a create-OR-UPDATE:
  // the first edit creates the one occurrence; every edit after that updates
  // the SAME occurrence (matched by `draft._id`) instead of leaving it behind
  // as an orphaned, never-saved second entry — the "I can add 2 notes" bug.
  // Multi-instance mappings are intentionally excluded: they use the explicit
  // "Log Violation" / "Add another occurrence" flow (`addOccurrence()` below)
  // so the inspector controls exactly when a new occurrence is created.
  //
  // IMPORTANT: `violation`, `onChange`, and `mapping` are read via a ref
  // instead of being effect dependencies. `mapping` in particular is a fresh
  // object every render (`getEffectiveMapping()` spreads it in the parent),
  // and `violation` gets a new identity every time this effect's own
  // `onChange` call causes the host to re-render. Depending on them directly
  // caused an infinite render loop (the app-hang bug): effect fires ->
  // onChange -> parent re-renders with new `violation`/`mapping` identities
  // -> effect dependency changed -> effect fires again -> forever. Reading
  // them from a ref keeps the effect reactive ONLY to actual draft edits.
  const latestRef = useRef({ violation, onChange, mapping, fieldId, formId, eventIndex, inspectorId });
  latestRef.current = { violation, onChange, mapping, fieldId, formId, eventIndex, inspectorId };

  /**
   * @logging persistSingleOccurrence
   * Events: violation_occurrence_logged.
   * Level: info.
   * Correlation: formId and eventIndex.
   */
  useEffect(() => {
    if (allowMultiple || !isFailing || readOnly || !draftSatisfiesGates) return;
    if (!(draft.notes?.trim() || draft.attachments.length > 0 || draft.location?.trim() || draft.correctiveActions?.trim())) return;

    const {
      violation: latestViolation, onChange: latestOnChange, mapping: latestMapping,
      fieldId: latestFieldId, formId: latestFormId, eventIndex: latestEventIndex, inspectorId: latestInspectorId,
    } = latestRef.current;

    const base = latestViolation ?? makeEmptyViolation({ fieldId: latestFieldId, formId: latestFormId, mapping: latestMapping, eventIndex: latestEventIndex });
    const existing = base.occurrences.find((o) => o._id === draft._id);

    // Skip no-op writes — guards the loop above even further by never calling
    // onChange when the occurrence already matches the draft content.
    if (
      existing &&
      existing.notes === draft.notes &&
      existing.location === draft.location &&
      existing.correctiveActions === draft.correctiveActions &&
      existing.attachments.length === draft.attachments.length &&
      existing.attachments.every((a, i) => a._id === draft.attachments[i]?._id)
    ) {
      return;
    }

    const occurrence: LoggedViolationOccurrence = {
      ...draft,
      discoveredBy: draft.discoveredBy || latestInspectorId,
      discoveredInEventIndex: draft.discoveredInEventIndex ?? latestEventIndex,
    };
    const nextOccurrences = existing
      ? base.occurrences.map((o) => (o._id === draft._id ? occurrence : o))
      : [...base.occurrences, occurrence];

    latestOnChange({ ...base, formId: latestFormId || base.formId, occurrences: nextOccurrences });
    if (!existing) {
      emitInspectionTelemetryEvent('violation_occurrence_logged', {
        formId: latestFormId,
        fieldId: latestFieldId,
        codeSetId: latestMapping.codeSetId,
        code: latestMapping.code,
        occurrenceCount: nextOccurrences.filter((o) => o.correctionStatus !== 'retracted').length,
        eventIndex: latestEventIndex,
      });
    }
  }, [allowMultiple, isFailing, readOnly, draftSatisfiesGates, draft, emitInspectionTelemetryEvent]);

  function toggleExpand(occId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(occId)) { next.delete(occId); } else { next.add(occId); }
      return next;
    });
  }

  /**
   * @logging addOccurrence
   * Events: violation_occurrence_logged.
   * Level: info.
   * Correlation: formId and eventIndex.
   */
  function addOccurrence() {
    if (readOnly || !allowNewOccurrence || !draftSatisfiesGates) {
      return;
    }
    const base = violation ?? makeEmptyViolation({ fieldId, formId, mapping, eventIndex });
    const occurrence: LoggedViolationOccurrence = {
      ...draft,
      discoveredBy: inspectorId,
      discoveredInEventIndex: eventIndex,
    };
    const next: LoggedViolation = { ...base, formId: formId || base.formId, occurrences: [...base.occurrences, occurrence] };
    onChange(next);
    emitInspectionTelemetryEvent('violation_occurrence_logged', {
      formId,
      fieldId,
      codeSetId: mapping.codeSetId,
      code: mapping.code,
      occurrenceCount: next.occurrences.filter((o) => o.correctionStatus !== 'retracted').length,
      eventIndex,
    });
    emitInspectionAuditEvent({
      kind: 'violation_occurrence_logged',
      fieldId,
      violationId: base._id,
      violationCode: mapping.code,
      occurrenceId: occurrence._id,
      occurrenceIndex: base.occurrences.length,
      location: occurrence.location,
    }, readOnly);
    // Reset the draft + collapse the form.
    setDraft(makeEmptyOccurrence(inspectorId, eventIndex));
    setIsDraftOpen(false);
  }

  function removeOccurrence(occId: string) {
    if (readOnly || !violation) return;
    const removedOccurrence = violation.occurrences.find((occurrence) => occurrence._id === occId);
    if (!removedOccurrence) return;
    const occurrenceIndex = violation.occurrences.findIndex((occurrence) => occurrence._id === occId);
    // P7 — soft-retract a CARRIED occurrence (one discovered in a prior event)
    // instead of hard-deleting it, so it survives the completion rollup as a
    // resolved tombstone rather than being resurrected from the prior event
    // snapshot. Same-event occurrences (no prior snapshot) are still hard-removed.
    const { remainingOccurrences, dropViolation } = removeOccurrenceFromViolation(
      violation.occurrences,
      occId,
      violation.source,
      { currentEventIndex: eventIndex, fixedBy: inspectorId, fixedAt: new Date().toISOString() },
    );
    if (dropViolation || remainingOccurrences.length === 0) {
      onChange(null);
    } else {
      onChange({ ...violation, status: getViolationStatus(remainingOccurrences), occurrences: remainingOccurrences });
    }
    emitInspectionAuditEvent({
      kind: 'violation_occurrence_removed',
      fieldId,
      violationId: violation._id,
      violationCode: mapping.code,
      occurrenceId: removedOccurrence._id,
      occurrenceIndex,
      location: removedOccurrence.location,
    }, readOnly);
  }

  /**
   * @logging markOccurrenceRepaired
   * Events: violation_occurrence_repaired.
   * Level: info.
   * Correlation: formId and eventIndex.
   */
  function markOccurrenceRepaired(occId: string, data: OccurrenceRepairData) {
    if (readOnly || !violation) return;
    const occurrences = violation.occurrences.map((o) =>
      o._id === occId
        ? applyOccurrenceCorrection(o, {
            correctionType: 'repaired',
            fixedBy: inspectorId,
            fixedAt: data.fixedAt,
            fixedInEventIndex: eventIndex,
            fixedNotes: data.repairNotes,
          })
        : o,
    );
    onChange({ ...violation, status: getViolationStatus(occurrences), occurrences });
    emitInspectionTelemetryEvent('violation_occurrence_repaired', {
      formId,
      fieldId,
      codeSetId: mapping.codeSetId,
      code: mapping.code,
      occurrenceCount: occurrences.filter((o) => o.correctionStatus !== 'retracted').length,
      eventIndex,
    });
  }

  function reopenOccurrence(occId: string) {
    if (readOnly || !violation) return;
    const occurrences = violation.occurrences.map((o) =>
      o._id === occId ? clearOccurrenceCorrection(o) : o,
    );
    onChange({ ...violation, status: getViolationStatus(occurrences), occurrences });
  }

  async function captureAttachment() {
    if (!attachmentPicker || readOnly) return;
    setCaptureError(null);
    try {
      const refs = await attachmentPicker.capture({
        parentType: 'occurrence',
        parentId: draft._id,
        kind: 'photo',
      });
      setDraft((d) => ({ ...d, attachments: dedupeById([...d.attachments, ...refs]) }));
    } catch (err) {
      // Without a surface the rejection is invisible — the button appears
      // to do nothing (the exact "can't add photos" symptom a host upload
      // failure would produce).
      setCaptureError((err as Error)?.message ?? 'Photo upload failed');
    }
  }

  function removeDraftAttachment(attachmentId: string) {
    if (readOnly) return;
    setDraft((d) => ({ ...d, attachments: d.attachments.filter((a) => a._id !== attachmentId) }));
  }

  if (readOnly && !isFailing && !hasOccurrences && !draft.notes?.trim() && draft.attachments.length === 0) {
    return null;
  }

  return (
    <div
      data-violation-block="true"
      data-field-id={fieldId}
      data-mapping-code={mapping.code}
      data-mapping-codeset={mapping.codeSetId}
      data-occurrence-count={visibleOccurrences.length}
      data-read-only={readOnly ? 'true' : 'false'}
      data-allow-new-occurrence={allowNewOccurrence ? 'true' : 'false'}
      data-draft-open={showDraftForm ? 'true' : 'false'}
      className="mt-1"
    >
      {/* ── Streamline-style Action Bar Strip ── */}
      <div className="flex flex-wrap items-center gap-3 text-xs font-medium my-1">
        {/* 1. Violation Citation / Description (when failing & bound mapping).
            Unmapped failures show the "pass/fail only" placeholder instead —
            there is no code reference to cite. */}
        {isFailing && !isUnmapped && (
          <div className="flex items-center gap-1.5 text-red-600 font-semibold shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>{violationTitle}</span>
            {codeLabel && <span className="text-[10px] font-mono text-red-500">({codeLabel})</span>}
          </div>
        )}
        {isFailing && isUnmapped && (
          <div
            data-unmapped-failure="true"
            className="flex items-center gap-1.5 text-red-600 font-semibold shrink-0"
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>This is &ldquo;pass&rdquo; or &ldquo;fail&rdquo; only, no code reference</span>
          </div>
        )}

        {isOffline && (
          <span role="status" data-pill="offline" className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
            offline
          </span>
        )}

        {/* 2. Add Notes / Notes Required Button */}
        {showNotes && (!readOnly || !!draft.notes?.trim()) && (
          <button
            type="button"
            onClick={() => setIsNotesOpen((open) => !open)}
            className={`inline-flex items-center gap-1 hover:underline cursor-pointer shrink-0 ${
              isNotesRequiredUnmet
                ? 'text-red-600 font-semibold'
                : draft.notes?.trim()
                  ? 'text-gray-800 font-medium'
                  : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Pencil className={`w-3.5 h-3.5 shrink-0 ${isNotesRequiredUnmet ? 'text-red-600' : 'text-gray-500'}`} />
            <span className="truncate max-w-[220px]">
              {isNotesRequiredUnmet
                ? 'Notes Required *'
                : draft.notes?.trim()
                  ? draft.notes.trim()
                  : 'Add Notes'}
            </span>
          </button>
        )}

        {/* 3. Add Photos / Photos Required Button */}
        {showPhotos && (!readOnly || draft.attachments.length > 0) && (
          <button
            type="button"
            onClick={() => setIsPhotosOpen((open) => !open)}
            className={`inline-flex items-center gap-1 hover:underline cursor-pointer shrink-0 ${
              isPhotosRequiredUnmet
                ? 'text-red-600 font-semibold'
                : draft.attachments.length > 0
                  ? 'text-gray-800 font-medium'
                  : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Camera className={`w-3.5 h-3.5 ${isPhotosRequiredUnmet ? 'text-red-600' : 'text-gray-500'}`} />
            <span>
              {isPhotosRequiredUnmet
                ? 'Photos Required *'
                : draft.attachments.length > 0
                  ? `${draft.attachments.length} Photo(s)`
                  : 'Add Photos'}
            </span>
          </button>
        )}
      </div>

      {/* ── Expanded Inline Notes Input — header with Clear Text / Close
          follows the Streamline "Notes" popover treatment so editing feels familiar.
          Unmapped failures open it automatically: the pass/fail state IS the
          violation, so the full capture surface (notes, photos, location) is
          presented at once. ── */}
      {showNotes && (isNotesOpen || (!readOnly && showDraftForm && (isNotesRequiredUnmet || isUnmapped))) && (
        <div className="mt-1 mb-2 border border-gray-200 rounded-lg bg-white p-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-gray-700">Notes</span>
            <div className="flex items-center gap-2">
              {!!draft.notes && !readOnly && (
                <button
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, notes: '' }))}
                  data-action="clear-notes"
                  className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer"
                >
                  Clear Text
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsNotesOpen(false)}
                aria-label="Close notes"
                data-action="close-notes"
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <XIcon size={14} />
              </button>
            </div>
          </div>
          <textarea
            autoFocus={!readOnly}
            readOnly={readOnly}
            disabled={readOnly}
            value={draft.notes ?? ''}
            onChange={(e) => { if (!readOnly) setDraft((d) => ({ ...d, notes: e.target.value })); }}
            placeholder={readOnly ? 'No notes' : 'Enter notes...'}
            rows={3}
            className={`w-full text-xs p-2 border border-gray-300 rounded-sm resize-none outline-hidden focus:outline-hidden ${
              readOnly ? 'bg-gray-50 text-gray-700 cursor-default' : 'bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-400'
            }`}
          />
        </div>
      )}

      {/* ── Photo thumbnails — ALWAYS visible once photos exist, independent
          of the "Add Photos" toggle below, so captured evidence is never
          hidden behind an extra tap. Tap a thumbnail to view full-screen;
          the remove (✕) button is always available — it must not
          require opening the "Add Photos" panel first, or there'd be no way
          to delete a photo once that panel is closed. ── */}
      {showPhotos && canShowThumbnails && draft.attachments.length > 0 && (
        <div className="mt-1 mb-1 flex flex-wrap gap-2" data-photo-thumbnails="draft">
          {draft.attachments.map((r, idx) => (
            <div key={r._id} className="relative group">
              <AttachmentThumb
                attachmentId={r._id}
                alt={r.fileName ?? `Photo ${idx + 1}`}
                sizeClassName="w-16 h-16"
                onClick={() => setLightbox({ attachments: draft.attachments, index: idx })}
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeDraftAttachment(r._id)}
                  aria-label="Remove photo"
                  data-action="remove-draft-photo"
                  className="absolute -top-1.5 -right-1.5 w-11 h-11 min-w-[44px] min-h-[44px] rounded-full bg-gray-800/80 text-white flex items-center justify-center"
                >
                  <XIcon size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Expanded "Add Photos" control — capture more evidence. Unmapped
          failures open it automatically (full capture surface). Hidden in readOnly mode. ── */}
      {!readOnly && showPhotos && (isPhotosOpen || (showDraftForm && (isPhotosRequiredUnmet || isUnmapped))) && (
        <div className="mt-1 mb-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={captureAttachment}
            disabled={!attachmentPicker || readOnly}
            data-action="capture-photo"
            className="px-2.5 py-1.5 text-xs border border-gray-300 bg-white rounded-lg hover:bg-gray-50 active:scale-95 flex items-center gap-1 cursor-pointer disabled:opacity-50 transition-all"
          >
            <Camera className="w-3.5 h-3.5 text-gray-600" />
            <span>+ Photo {requirePhotos && !readOnly && <span aria-label="required" className="text-red-500">*</span>}</span>
          </button>
          {draft.attachments.length > 0 && (
            <span data-photo-count={draft.attachments.length} className="text-xs text-gray-500">
              {draft.attachments.length} photo{draft.attachments.length === 1 ? '' : 's'}
            </span>
          )}
          {captureError && (
            <span role="alert" data-capture-error="true" className="text-xs text-red-600">
              {captureError}
            </span>
          )}
        </div>
      )}

      {/* ── Draft form (location / corrective action). For single-instance
          mappings these fields auto-save via the effect above — no explicit
          "Log Violation" click needed, so the button only appears for
          multi-instance mappings where the inspector decides exactly when a
          new occurrence is created. Location capture is ALWAYS part of the
          form (the deprecated `showLocation` prop documents this contract);
          `requireLocation` only drives the required-asterisk and the log gate. ── */}
      {isFailing && showDraftForm && (
        <div className="border border-red-200 bg-red-50/40 rounded-sm p-2 mt-1" data-violation-draft-form="true">
          <label className="text-xs text-gray-700 block mb-1">
            Location {requireLocation && <span aria-label="required" className="text-red-500">*</span>}
            <input
              type="text"
              value={draft.location ?? ''}
              onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
              aria-invalid={!locationOk}
              className="mt-0.5 w-full px-2 py-1 text-xs border border-gray-300 rounded-sm bg-white outline-hidden focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
            />
            {quickPickLocations.length > 0 && (
              <span className="mt-1 flex flex-wrap gap-1" data-quick-picks="locations">
                {quickPickLocations.map((location) => (
                  <button
                    key={location}
                    type="button"
                    onClick={() => setDraft((current) => ({ ...current, location }))}
                    className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-100"
                  >
                    {location}
                  </button>
                ))}
              </span>
            )}
          </label>
          {showCorrectiveAction && (
            <label className="text-xs text-gray-700 block mb-1">
              Corrective action {requireCorrective && <span aria-label="required" className="text-red-500">*</span>}
              <input
                type="text"
                value={draft.correctiveActions ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, correctiveActions: e.target.value }))}
                aria-invalid={requireCorrective && !correctiveOk}
                className="mt-0.5 w-full px-2 py-1 text-xs border border-gray-300 rounded-sm bg-white outline-hidden focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-400"
              />
              {quickPickRemedies.length > 0 && (
                <span className="mt-1 flex flex-wrap gap-1" data-quick-picks="remedies">
                  {quickPickRemedies.map((remedy) => (
                    <button
                      key={remedy}
                      type="button"
                      onClick={() => setDraft((current) => ({ ...current, correctiveActions: remedy }))}
                      className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-100"
                    >
                      {remedy}
                    </button>
                  ))}
                </span>
              )}
            </label>
          )}
          {allowMultiple ? (
            <button
              type="button"
              onClick={addOccurrence}
              disabled={!draftSatisfiesGates}
              data-action="log-violation"
              className="mt-1 px-3 py-1 text-xs font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer"
            >
              Save &amp; Shrink Occurrence #{occurrenceCount + 1}
            </button>
          ) : hasOccurrences && (
            <p className="mt-1 text-[10px] text-gray-400">Saved automatically</p>
          )}
        </div>
      )}

      {isFailing && readOnly && (draft.location || draft.correctiveActions) && (
        <div className="text-xs text-gray-700 space-y-1 mt-1 border border-gray-200 bg-gray-50 rounded-sm p-2">
          {draft.location && <div><span className="font-semibold text-gray-600">Location:</span> {draft.location}</div>}
          {draft.correctiveActions && <div><span className="font-semibold text-gray-600">Corrective action:</span> {draft.correctiveActions}</div>}
        </div>
      )}

      {/* ── Occurrence list — compact rows, click to expand. Multi-instance
          mappings only: single-instance mappings edit their one occurrence
          directly via the action bar / draft form above, so repeating the
          same notes/photos in a list here would just be the "two note boxes"
          bug again. Single-instance repair/remove controls are the compact
          status row further below. ── */}
      {allowMultiple && visibleOccurrences.length > 0 && (
        <>
          <div className="mt-2 pt-1 flex items-center gap-1.5">
            {unrepairedCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-red-100 text-red-700">
                {unrepairedCount} unrepaired
              </span>
            )}
            {repairedCount > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-green-100 text-green-700">
                {repairedCount} repaired
              </span>
            )}
          </div>
          <ul role="list" data-occurrences className="flex flex-col divide-y divide-red-100 border-t border-red-200 pt-1">
          {visibleOccurrences.map((o, idx) => {
            const isExpanded = expandedIds.has(o._id);
            const isRepaired = o.status === 'resolved' || o.status === 'closed';
            return (
              <li
                key={o._id}
                role="listitem"
                data-occurrence-id={o._id}
                className="text-xs text-gray-800"
              >
                {/* Collapsed one-line row */}
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 py-1.5 min-w-0">
                  {/* Expand/collapse toggle */}
                  <button
                    type="button"
                    onClick={() => toggleExpand(o._id)}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse occurrence detail' : 'Expand occurrence detail'}
                    className="shrink-0 text-gray-400 hover:text-gray-600"
                  >
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                  {/* Occurrence index */}
                  <span className="shrink-0 text-[10px] font-semibold text-gray-400 w-4 text-right">
                    {idx + 1}.
                  </span>
                  {/* Location, falling back to notes, then a plain occurrence label —
                      never a bare "no location" placeholder, which reads like an error. */}
                  <span className="flex-1 min-w-[8rem] truncate text-gray-700 font-medium">
                    {o.location || o.notes || `Occurrence ${idx + 1}`}
                  </span>
                  {/* Status pill */}
                  {isRepaired ? (
                    <span className="shrink-0 px-1 py-0.5 rounded-sm text-[9px] font-semibold bg-green-100 text-green-700">
                      Repaired
                    </span>
                  ) : (
                    <span className="shrink-0 px-1 py-0.5 rounded-sm text-[9px] font-semibold bg-amber-100 text-amber-700">
                      Open
                    </span>
                  )}
                  {/* Photo count badge — tap to open the first photo full-screen */}
                  {o.attachments.length > 0 && !canShowThumbnails && (
                    <button
                      type="button"
                      onClick={() => setLightbox({ attachments: o.attachments, index: 0 })}
                      aria-label={`View ${o.attachments.length} photo${o.attachments.length === 1 ? '' : 's'}`}
                      className="shrink-0 flex items-center gap-0.5 text-[9px] text-gray-500 hover:text-gray-700"
                    >
                      <Camera size={11} />
                      {o.attachments.length}
                    </button>
                  )}
                  {/* Inline repair/undo actions */}
                  <OccurrenceRepairBlock
                    occurrence={o}
                    label={o.location}
                    violationCode={mapping.code}
                    fieldId={fieldId}
                    violationId={violation?._id}
                    occurrenceIndex={idx}
                    eventIndex={eventIndex}
                    readOnly={readOnly}
                    currentUserId={inspectorId}
                    onMarkRepaired={markOccurrenceRepaired}
                    onReopen={reopenOccurrence}
                    compact={true}
                  />
                  {/* Remove button */}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => setPendingRemovalId(o._id)}
                      data-action="remove-occurrence"
                      className="shrink-0 text-[10px] text-red-400 hover:text-red-700 ml-1"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Photo thumbnails — ALWAYS visible when present, not hidden
                    behind the expand chevron, so evidence is never a click away. */}
                {o.attachments.length > 0 && canShowThumbnails && (
                  <div className="ml-7 pb-1.5 flex flex-wrap gap-1.5" data-photo-thumbnails={`occurrence-${o._id}`}>
                    {o.attachments.map((a, photoIdx) => (
                      <AttachmentThumb
                        key={a._id}
                        attachmentId={a._id}
                        alt={a.fileName ?? `Photo ${photoIdx + 1}`}
                        sizeClassName="w-14 h-14"
                        onClick={() => setLightbox({ attachments: o.attachments, index: photoIdx })}
                      />
                    ))}
                  </div>
                )}

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="ml-7 pb-2 space-y-1 text-xs text-gray-600 border-l-2 border-red-100 pl-3">
                    {o.notes && (
                      <div><span className="font-medium text-gray-700">Notes:</span> {o.notes}</div>
                    )}
                    {o.correctiveActions && (
                      <div><span className="font-medium text-gray-700">Corrective action:</span> {o.correctiveActions}</div>
                    )}
                    {o.discoveredAt && (
                      <div className="text-[10px] text-gray-400">
                        Logged {new Date(o.discoveredAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        {o.discoveredBy && ` · ${o.discoveredBy}`}
                      </div>
                    )}
                    {isRepaired && o.fixedAt && (
                      <div className="text-[10px] text-green-600">
                        Repaired {new Date(o.fixedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        {o.fixedBy && ` · ${o.fixedBy}`}
                        {o.fixedNotes && ` — ${o.fixedNotes}`}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          </ul>
        </>
      )}

      {/* ── Single-instance status row — repair/reopen + remove, bound to the
          one occurrence that the action bar above already edits. No repeated
          notes/photos here (they're already visible above); this row only
          surfaces the repair workflow and a way to remove the entry. Shown
          while the field is actively failing OR while the occurrence is still
          open — an inspector who flips the answer back to passing must still
          be able to repair (or remove) the occurrence they logged; hiding
          repair behind the pass state would orphan open violations. ── */}
      {!allowMultiple && visibleOccurrences[0] && !(
        !isFailing &&
        visibleOccurrences[0]._id.startsWith('occ_') &&
        visibleOccurrences[0].discoveredBy === 'legacy-migration'
      ) && (isFailing || visibleOccurrences[0].status === 'open') && (() => {
        const o = visibleOccurrences[0];
        return (
          <div className="mt-2 pt-2 border-t border-red-100 flex items-center gap-2 text-xs" data-occurrence-id={o._id}>
            <OccurrenceRepairBlock
              occurrence={o}
              label={o.location}
              violationCode={mapping.code}
              fieldId={fieldId}
              violationId={violation?._id}
              occurrenceIndex={0}
              eventIndex={eventIndex}
              readOnly={readOnly}
              currentUserId={inspectorId}
              onMarkRepaired={markOccurrenceRepaired}
              onReopen={reopenOccurrence}
              compact={true}
            />
            {!readOnly && (
              <button
                type="button"
                onClick={() => setPendingRemovalId(o._id)}
                data-action="remove-occurrence"
                className="shrink-0 text-[10px] text-red-400 hover:text-red-700 ml-auto"
              >
                Remove
              </button>
            )}
          </div>
        );
      })()}

      {/* ── Add another occurrence — only when the mapping allows multiple
          instances and the field is still failing. ── */}
      {showAddAnother && (
        <button
          type="button"
          onClick={() => setIsDraftOpen(true)}
          data-action="add-another-occurrence"
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-sm border border-dashed border-red-300 px-3 py-2 text-xs font-semibold text-red-700 hover:border-red-400 hover:bg-red-50 cursor-pointer"
        >
          <Plus size={12} />
          + Add Another Occurrence
          <span className="ml-1 font-normal text-red-600">{mapping.code}</span>
        </button>
      )}

      {pendingRemovalId && (
        <TypeYesConfirmModal
          title="Remove this violation occurrence?"
          description={
            <span>
              You are about to permanently remove a logged violation
              occurrence. This cannot be undone.
            </span>
          }
          confirmButtonLabel="Yes, remove"
          onClose={() => setPendingRemovalId(null)}
          onConfirm={() => {
            const id = pendingRemovalId;
            setPendingRemovalId(null);
            removeOccurrence(id);
          }}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          attachments={lightbox.attachments}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function dedupeById(refs: AttachmentRefLike[]): AttachmentRefLike[] {
  const seen = new Set<string>();
  const out: AttachmentRefLike[] = [];
  for (const r of refs) {
    if (seen.has(r._id)) continue;
    seen.add(r._id);
    out.push(r);
  }
  return out;
}
